import '../../../scss/hotfood/components/hotfood-fable.scss'

import {
  Body1,
  Box,
  Button,
  ButtonGroup,
  ButtonGroupSection,
  Input,
  Label2,
  Modal,
} from '@sainsburys-tech/fable'
import { Close } from '@sainsburys-tech/icons'
import React, { useEffect, useState } from 'react'

import { useApiClient } from '../../../common/AppContext/appContext'
import { getUserStore } from '../../../utils/localStore'
import { FormErrorMessageKey } from '../../enums/FormErrorMessageKey'
import { BatchTray, Shelf } from '../../enums/TemperatureCheckPosition'
import { IInputModalProps } from '../../types/IInputModalProps'
import { ITemperatureCheckValidationProps } from '../../types/ITemperatureCheckValidationProps'
import { ITemperatureData } from '../../types/ITemperatureData'
import { validateTemperatures } from '../../utils/hotFoodUtils'
import TemperatureCheckErrorMessage from '../common/TemperatureCheckErrorMessage'

const TemperatureCheckModal = (props: IInputModalProps) => {
  const storeId = getUserStore().storeId
  const [temperatures, setTemperatures] = useState<ITemperatureData[]>([])
  const [validation, setValidation] =
    useState<ITemperatureCheckValidationProps>({
      errorData: [],
      isLowReadingWarned: false,
      messageKey: FormErrorMessageKey.NONE,
      result: true,
    })

  const apiClient = useApiClient()
  const [isBluetoothReading, setIsBluetoothReading] = useState(true)

  useEffect(() => {
    const trayTemperatures: ITemperatureData[] = [
      { position: BatchTray.Top, temperature: '' },
    ]

    if (props.requiredTemperatureCount > 1) {
      trayTemperatures.push({ position: BatchTray.Middle, temperature: '' })
    }

    if (props.requiredTemperatureCount > 2) {
      trayTemperatures.push({ position: BatchTray.Bottom, temperature: '' })
    }
    setTemperatures(trayTemperatures)

    const probeEnabled = true

    setIsBluetoothReading(true)

    const script: HTMLScriptElement = document.createElement('script')
    script.src = './probe.js'
    document.head.append(script)

    if (
      (window as Window).webkit &&
      (window as Window).webkit!.messageHandlers.bluetoothMessage
    ) {
      ;(window as Window).webkit!.messageHandlers.bluetoothMessage.postMessage(
        'start',
      )
    }

    return () => {
      if (probeEnabled) {
        document.head.removeChild(script)
        if (
          (window as Window).webkit &&
          (window as Window).webkit!.messageHandlers.bluetoothMessage
        ) {
          ;(
            window as Window
          ).webkit!.messageHandlers.bluetoothMessage.postMessage('stop')
        }
      }
    }
  }, [apiClient, props.requiredTemperatureCount, storeId])

  const renderTemperatureInputText = (position: BatchTray | Shelf) => {
    const hasError =
      validation.errorData.filter((t) => t.position === position).length > 0
    return (
      <div>
        <Input
          label={`Temperature (${position})`}
          inputProps={{
            id: `temperature-input-${position}`,
          }}
          testid={`temperature-input-${position}`}
          autocomplete='off'
          type='number'
          value={getUpdatedTemperatureValue(position)}
          isReadOnly={isBluetoothReading}
          placeholder='--.- ℃'
          status={
            hasError
              ? { message: 'Invalid temperature', type: 'error' }
              : undefined
          }
          onChange={(value) => {
            // Simulate the event object for the existing handler
            const fakeEvent = {
              currentTarget: { value },
            } as React.FormEvent<HTMLInputElement>
            handleTemperatureValidation(fakeEvent, position)
          }}
        />
      </div>
    )
  }

  // validate the temperature in a xx.x format and each dynamic temperature inputs are updated on the matching object.
  const handleTemperatureValidation = (
    e: React.FormEvent<HTMLInputElement>,
    position: BatchTray | Shelf,
  ) => {
    const targets = temperatures.reduce(
      (accTemperatures: ITemperatureData[], current) => {
        if (current.position === position) {
          const matches = e.currentTarget.value.match(
            /(\d{0,3}[.]\d)|(\d{0,3}[.])|([\d]{1,3})/,
          )

          if (!matches || matches.length === 0) {
            current.temperature = ''
            return [...accTemperatures, current]
          }

          if (parseFloat(e.currentTarget.value) <= 140.0) {
            current.temperature = matches[0]
          }

          return [...accTemperatures, current]
        }

        return [...accTemperatures, current]
      },
      [],
    )
    setTemperatures(targets)
  }

  const getUpdatedTemperatureValue = (position: BatchTray | Shelf) => {
    const targetData = temperatures.find((t) => t.position === position)
    if (!targetData) {
      return ''
    }

    return targetData.temperature
  }

  const onSave = async () => {
    const [errorData, messageKey, result] = validateTemperatures(temperatures)
    setValidation({
      errorData: errorData as ITemperatureData[],
      isLowReadingWarned:
        !validation.isLowReadingWarned &&
        !result &&
        messageKey === FormErrorMessageKey.Under20DegTemperature,
      messageKey: messageKey as FormErrorMessageKey,
      result: result as boolean,
    })

    if (!result && !validation.isLowReadingWarned) {
      return
    }
    await props.handleSave(temperatures, isBluetoothReading)
  }

  const switchInputModeToBluetooth = (bool: boolean) => {
    setIsBluetoothReading(bool)
    const updatedTemperatureDataArray = temperatures.map((item) => ({
      ...item,
      temperature: '',
    }))
    setTemperatures(updatedTemperatureDataArray)
  }

  return (
    <Modal
      size='md'
      onClose={() => props.handleClose()}
      isOpen={props.isOpen}
      testid='info-modal'
      header={{ label: props.title }}
    >
      <Button
        icon={<Close />}
        onClick={() => props.handleClose()}
        variant='tertiary'
        className='c-modal-close'
        aria-label='Close modal'
        style={{
          position: 'absolute',
          top: '1rem',
          right: '1rem',
          color: 'black',
          paddingRight: '24px',
        }}
        data-testid='close-modal-button'
      />
      <div>
        <span id='probe-status' data-testid='probe-status'></span>
      </div>
      <Body1 data-testid='modalDescription' style={{ marginBottom: '1rem' }}>
        {props.description}
      </Body1>
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
        padding='none'
      >
        <div>
          {isBluetoothReading ? (
            <>
              <Label2
                data-testid='manual-input-hint'
                style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              >
                Can't use your Bluetooth probe?
              </Label2>
              <Button
                data-testid='manual-input-button'
                onClick={() => switchInputModeToBluetooth(false)}
                testid='manual-input-button'
                variant={'tertiary'}
                style={{ marginBottom: '1rem' }}
              >
                Change to manual checks
              </Button>
            </>
          ) : (
            <Button
              data-testid='bluetooth-input-button'
              onClick={() => switchInputModeToBluetooth(true)}
              testid='bluetooth-button'
              style={{ marginBottom: '1rem' }}
            >
              Change back to Bluetooth checks
            </Button>
          )}
        </div>
        <Box
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1.5rem',
          }}
          padding='none'
        >
          {temperatures.map((item, index) => (
            <div
              className='c-common-incrementor__input-text-box'
              style={{ width: '100%' }}
            >
              <Input
                key={index}
                label={`Temperature (${item.position})`}
                hasHiddenLabel
                inputProps={{
                  id: `temperature-input-${item.position}`,
                }}
                testid={`temperature-input-${item.position}`}
                autocomplete='off'
                type='text'
                inputMode='decimal'
                pattern='^\d{1,3}\.\d+$'
                value={getUpdatedTemperatureValue(item.position)}
                isReadOnly={isBluetoothReading}
                placeholder='--.- ℃'
                status={
                  validation.errorData.filter(
                    (t) => t.position === item.position,
                  ).length > 0
                    ? { message: '', type: 'error' }
                    : undefined
                }
                onChange={(value) => {
                  const fakeEvent = {
                    currentTarget: { value },
                  } as React.FormEvent<HTMLInputElement>
                  handleTemperatureValidation(fakeEvent, item.position)
                }}
                style={{
                  maxWidth: '120px',
                  width: '100%',
                  margin: '10px auto',
                  fontSize: '1rem',
                }}
              />
            </div>
          ))}
        </Box>
        {!validation.result && (
          <TemperatureCheckErrorMessage
            class=''
            messageKey={validation.messageKey}
          />
        )}
        <ButtonGroup>
          <ButtonGroupSection className='hotfood-full-width'>
            <Button
              className='c-form-buttom-center c-submit-modal-button hotfood-full-width'
              onClick={() => onSave()}
              testid='save-button'
              style={{ maxWidth: '180px', margin: '0 auto' }}
            >
              Save
            </Button>
          </ButtonGroupSection>
        </ButtonGroup>
      </Box>
    </Modal>
  )
}

export default TemperatureCheckModal
